import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Stack,
    IconButton,
    MenuItem,
    Autocomplete,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import EmployeePicker from "../../pickers/EmployeePicker";
import EngineeringService from "../../../services/Engineering.service";
import { PRIORITY_CONFIG, STATUS_CONFIG } from "./constants";

const EMPTY_FORM = {
    assigned_to: null,
    task_type: null,
    linkedTo: "none",
    project: null,
    quote: null,
    start_date: "",
    due_date: "",
    estimated_hours: "",
    priority: 2,
    status: "not_started",
    notes: "",
};

export default function ScheduleItemFormDialog({ open, onClose, item, onSaved }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [taskTypes, setTaskTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isEditing = Boolean(item);

    useEffect(() => {
        if (!open) return;
        EngineeringService.listTaskTypes()
            .then((res) => {
                const results = Array.isArray(res.data) ? res.data : [];
                setTaskTypes(results.filter((t) => t.is_active !== false));
            })
            .catch(() => setTaskTypes([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (item) {
            setForm({
                assigned_to: item.assigned_to_detail || null,
                task_type: item.task_type_detail || null,
                linkedTo: item.project_detail ? "project" : item.quote_detail ? "quote" : "none",
                project: item.project_detail ? { id: item.project, ...item.project_detail } : null,
                quote: item.quote_detail ? { id: item.quote, ...item.quote_detail } : null,
                start_date: item.start_date || "",
                due_date: item.due_date || "",
                estimated_hours: item.estimated_hours ?? "",
                priority: item.priority,
                status: item.status,
                notes: item.notes || "",
            });
        } else {
            setForm({ ...EMPTY_FORM });
        }
        setError("");
    }, [open, item]);

    const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

    const handleLinkedToChange = (_, val) => {
        if (!val) return;
        setForm((f) => ({ ...f, linkedTo: val, project: null, quote: null }));
    };

    const handleSave = async () => {
        if (!form.assigned_to) {
            setError("Please select an assigned engineer.");
            return;
        }
        if (!form.task_type) {
            setError("Please select a task type.");
            return;
        }

        const payload = {
            assigned_to: form.assigned_to.id,
            task_type: form.task_type.id,
            project: form.linkedTo === "project" ? form.project?.id : null,
            quote: form.linkedTo === "quote" ? form.quote?.id : null,
            start_date: form.start_date || null,
            due_date: form.due_date || null,
            estimated_hours: form.estimated_hours === "" ? null : form.estimated_hours,
            priority: form.priority,
            status: form.status,
            notes: form.notes,
        };

        setLoading(true);
        setError("");
        try {
            if (isEditing) {
                await EngineeringService.updateScheduleItem(item.id, payload);
            } else {
                await EngineeringService.createScheduleItem(payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                JSON.stringify(err?.response?.data) ||
                "An error occurred.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {isEditing ? "Edit Schedule Item" : "New Schedule Item"}
                    {fullScreen && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <EmployeePicker
                            label="Assigned To"
                            value={form.assigned_to}
                            onChange={set("assigned_to")}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            options={taskTypes}
                            value={form.task_type}
                            onChange={(_, val) => set("task_type")(val)}
                            getOptionLabel={(opt) => opt?.name || ""}
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Task Type"
                                    helperText={
                                        taskTypes.length === 0
                                            ? "No task types configured yet."
                                            : ""
                                    }
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Link to
                        </Typography>
                        <ToggleButtonGroup
                            value={form.linkedTo}
                            exclusive
                            onChange={handleLinkedToChange}
                            size="small"
                            fullWidth
                            sx={{ display: "flex", mt: 0.5 }}
                        >
                            <ToggleButton value="none" sx={{ flex: 1 }}>
                                None
                            </ToggleButton>
                            <ToggleButton value="project" sx={{ flex: 1 }}>
                                Project
                            </ToggleButton>
                            <ToggleButton value="quote" sx={{ flex: 1 }}>
                                Quote
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {form.linkedTo !== "none" && (
                        <Grid item xs={12}>
                            {form.linkedTo === "project" ? (
                                <ProjectPicker
                                    value={form.project}
                                    onChange={set("project")}
                                    label="Project"
                                />
                            ) : (
                                <QuotePicker
                                    value={form.quote}
                                    onChange={set("quote")}
                                    label="Quote"
                                />
                            )}
                        </Grid>
                    )}

                    <Grid item xs={6}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={form.start_date}
                            onChange={(e) => set("start_date")(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Due Date"
                            type="date"
                            value={form.due_date}
                            onChange={(e) => set("due_date")(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            select
                            label="Priority"
                            value={form.priority}
                            onChange={(e) => set("priority")(Number(e.target.value))}
                            fullWidth
                        >
                            {Object.entries(PRIORITY_CONFIG).map(([val, cfg]) => (
                                <MenuItem key={val} value={Number(val)}>
                                    {cfg.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            select
                            label="Status"
                            value={form.status}
                            onChange={(e) => set("status")(e.target.value)}
                            fullWidth
                        >
                            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                                <MenuItem key={val} value={val}>
                                    {cfg.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Estimated Hours"
                            type="number"
                            value={form.estimated_hours}
                            onChange={(e) => set("estimated_hours")(e.target.value)}
                            fullWidth
                            inputProps={{ min: 0, step: 0.25 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            value={form.notes}
                            onChange={(e) => set("notes")(e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={fullScreen ? { pb: "max(8px, env(safe-area-inset-bottom))" } : undefined}
            >
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {isEditing ? "Save Changes" : "Create Item"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
